import type { SVGProps } from 'react';

/**
 * A small stroke-based icon set. Hand-drawn rather than a dependency: the studio needs
 * about thirty glyphs, and a full icon package costs more bundle than it saves.
 *
 * Every icon is decorative — it always sits next to a text label, so it is hidden from
 * assistive technology and the label carries the meaning.
 */
export type IconName =
  | 'text' | 'paragraph' | 'at' | 'phone' | 'link' | 'hash' | 'currency'
  | 'radio' | 'checkbox' | 'chevronDown' | 'toggle' | 'scale' | 'rank'
  | 'star' | 'gauge' | 'slider' | 'grid' | 'gridCheck'
  | 'calendar' | 'clock' | 'calendarClock' | 'globe' | 'upload' | 'signature'
  | 'heading' | 'eyeOff' | 'function'
  | 'plus' | 'trash' | 'copy' | 'drag' | 'undo' | 'redo' | 'search'
  | 'check' | 'close' | 'alert' | 'info' | 'external' | 'sun' | 'chevronUp'
  | 'arrowUp' | 'arrowDown' | 'page' | 'sparkle'
  | 'bell' | 'building' | 'shield' | 'ticket' | 'switch' | 'mail' | 'users'
  | 'chevronLeft' | 'chevronRight' | 'palette' | 'image'
  | 'help' | 'book' | 'menu' | 'pencil' | 'more';

const paths: Record<IconName, React.ReactNode> = {
  menu: <><path d="M4 7h16" /><path d="M4 12h16" /><path d="M4 17h16" /></>,
  text: <><path d="M4 7V5h16v2" /><path d="M12 5v14" /><path d="M9 19h6" /></>,
  paragraph: <><path d="M4 6h16" /><path d="M4 11h16" /><path d="M4 16h10" /></>,
  at: <><circle cx="12" cy="12" r="4" /><path d="M16 8v5a3 3 0 0 0 6 0v-1a10 10 0 1 0-4 8" /></>,
  phone: <path d="M5 4h4l2 5-2.5 1.5a11 11 0 0 0 5 5L15 13l5 2v4a2 2 0 0 1-2 2A16 16 0 0 1 3 6a2 2 0 0 1 2-2Z" />,
  link: <><path d="M10 13a5 5 0 0 0 7 0l3-3a5 5 0 0 0-7-7l-1 1" /><path d="M14 11a5 5 0 0 0-7 0l-3 3a5 5 0 0 0 7 7l1-1" /></>,
  hash: <><path d="M5 9h14" /><path d="M5 15h14" /><path d="M10 4 8 20" /><path d="M16 4l-2 16" /></>,
  currency: <><circle cx="12" cy="12" r="8" /><path d="M15 9.5a3 3 0 0 0-3-1.5c-1.7 0-3 .9-3 2s1.3 2 3 2 3 .9 3 2-1.3 2-3 2a3 3 0 0 1-3-1.5" /><path d="M12 6v12" /></>,

  radio: <><circle cx="12" cy="12" r="8" /><circle cx="12" cy="12" r="3" fill="currentColor" stroke="none" /></>,
  checkbox: <><rect x="4" y="4" width="16" height="16" rx="3" /><path d="m8.5 12 2.5 2.5 4.5-5" /></>,
  chevronDown: <path d="m6 9 6 6 6-6" />,
  chevronUp: <path d="m6 15 6-6 6 6" />,
  toggle: <><rect x="2" y="7" width="20" height="10" rx="5" /><circle cx="8" cy="12" r="2.5" fill="currentColor" stroke="none" /></>,
  scale: <><path d="M4 12h16" /><path d="M7 9v6" /><path d="M12 8v8" /><path d="M17 9v6" /></>,
  rank: <><path d="M4 6h9" /><path d="M4 12h13" /><path d="M4 18h7" /><path d="m18 15 3 3-3 3" /></>,

  star: <path d="m12 3 2.6 5.6 6 .8-4.4 4.2 1.1 6-5.3-2.9-5.3 2.9 1.1-6L3.4 9.4l6-.8Z" />,
  gauge: <><path d="M4 17a8 8 0 1 1 16 0" /><path d="m12 13 4-3" /></>,
  slider: <><path d="M4 8h16" /><path d="M4 16h16" /><circle cx="9" cy="8" r="2.5" fill="currentColor" stroke="none" /><circle cx="16" cy="16" r="2.5" fill="currentColor" stroke="none" /></>,

  grid: <><rect x="3" y="4" width="18" height="16" rx="2" /><path d="M3 10h18" /><path d="M9 10v10" /></>,
  gridCheck: <><rect x="3" y="4" width="18" height="16" rx="2" /><path d="M3 10h18" /><path d="M9 10v10" /><path d="m12 14 1.5 1.5 3-3" /></>,

  calendar: <><rect x="3" y="5" width="18" height="16" rx="2" /><path d="M3 10h18" /><path d="M8 3v4" /><path d="M16 3v4" /></>,
  clock: <><circle cx="12" cy="12" r="8" /><path d="M12 8v4l3 2" /></>,
  calendarClock: <><rect x="3" y="5" width="12" height="16" rx="2" /><path d="M3 10h12" /><circle cx="18" cy="16" r="4" /><path d="M18 14.5V16l1 1" /></>,
  globe: <><circle cx="12" cy="12" r="9" /><path d="M3 12h18" /><path d="M12 3a15 15 0 0 1 0 18a15 15 0 0 1 0-18Z" /></>,
  upload: <><path d="M12 16V4" /><path d="m7 9 5-5 5 5" /><path d="M4 17v2a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-2" /></>,
  signature: <><path d="M3 17c3 0 3-9 6-9s3 9 6 9 3-4 6-4" /><path d="M3 21h18" /></>,

  heading: <><path d="M6 5v14" /><path d="M18 5v14" /><path d="M6 12h12" /></>,
  eyeOff: <><path d="m3 3 18 18" /><path d="M10.6 10.6a2 2 0 0 0 2.8 2.8" /><path d="M9.4 5.3A9.6 9.6 0 0 1 12 5c5 0 9 4.5 9 7a11 11 0 0 1-2.4 3.5" /><path d="M6.2 6.6C4.2 8 3 10.2 3 12c0 2.5 4 7 9 7 1.3 0 2.5-.3 3.6-.8" /></>,
  function: <><path d="M14 4h-1a3 3 0 0 0-3 3v10a3 3 0 0 1-3 3H6" /><path d="M7 11h9" /></>,

  plus: <><path d="M12 5v14" /><path d="M5 12h14" /></>,
  trash: <><path d="M4 7h16" /><path d="M9 7V5a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2" /><path d="M6 7l1 13h10l1-13" /></>,
  copy: <><rect x="8" y="8" width="12" height="12" rx="2" /><path d="M16 8V6a2 2 0 0 0-2-2H6a2 2 0 0 0-2 2v8a2 2 0 0 0 2 2h2" /></>,
  drag: <><circle cx="9" cy="6" r="1.4" fill="currentColor" stroke="none" /><circle cx="15" cy="6" r="1.4" fill="currentColor" stroke="none" /><circle cx="9" cy="12" r="1.4" fill="currentColor" stroke="none" /><circle cx="15" cy="12" r="1.4" fill="currentColor" stroke="none" /><circle cx="9" cy="18" r="1.4" fill="currentColor" stroke="none" /><circle cx="15" cy="18" r="1.4" fill="currentColor" stroke="none" /></>,
  undo: <><path d="M4 12h11a4 4 0 0 1 0 8h-3" /><path d="m8 8-4 4 4 4" /></>,
  redo: <><path d="M20 12H9a4 4 0 0 0 0 8h3" /><path d="m16 8 4 4-4 4" /></>,
  search: <><circle cx="11" cy="11" r="6" /><path d="m20 20-4.5-4.5" /></>,

  check: <path d="m5 13 4.5 4.5L19 7" />,
  close: <><path d="m6 6 12 12" /><path d="m18 6-12 12" /></>,
  alert: <><path d="M12 4 2.5 20h19Z" /><path d="M12 10v4" /><path d="M12 17.5v.5" /></>,
  info: <><circle cx="12" cy="12" r="9" /><path d="M12 11v5" /><path d="M12 8v.5" /></>,
  external: <><path d="M14 4h6v6" /><path d="M20 4 10 14" /><path d="M18 14v5a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1V7a1 1 0 0 1 1-1h5" /></>,
  sun: <><circle cx="12" cy="12" r="4" /><path d="M12 2v2M12 20v2M4.9 4.9l1.4 1.4M17.7 17.7l1.4 1.4M2 12h2M20 12h2M4.9 19.1l1.4-1.4M17.7 6.3l1.4-1.4" /></>,

  arrowUp: <><path d="M12 19V5" /><path d="m6 11 6-6 6 6" /></>,
  arrowDown: <><path d="M12 5v14" /><path d="m6 13 6 6 6-6" /></>,
  page: <><path d="M6 3h8l4 4v14a1 1 0 0 1-1 1H6a1 1 0 0 1-1-1V4a1 1 0 0 1 1-1Z" /><path d="M14 3v4h4" /></>,
  sparkle: <><path d="m12 3 1.8 4.7L18.5 9.5l-4.7 1.8L12 16l-1.8-4.7L5.5 9.5l4.7-1.8Z" /><path d="M18.5 15.5 19.4 18l2.5.9-2.5.9-.9 2.5-.9-2.5-2.5-.9 2.5-.9Z" /></>,

  bell: <><path d="M6 9a6 6 0 1 1 12 0c0 4 1.5 5.5 2 6H4c.5-.5 2-2 2-6Z" /><path d="M10 19a2 2 0 0 0 4 0" /></>,
  building: <><path d="M4 21V6a1 1 0 0 1 1-1h8a1 1 0 0 1 1 1v15" /><path d="M14 10h5a1 1 0 0 1 1 1v10" /><path d="M7 9h4M7 13h4M7 17h4M17 14h1M17 18h1" /><path d="M2 21h20" /></>,
  shield: <><path d="M12 3l7 3v6c0 4.5-3 7.8-7 9-4-1.2-7-4.5-7-9V6Z" /><path d="m9 12 2 2 4-4" /></>,
  ticket: <><path d="M4 8a2 2 0 0 1 2-2h12a2 2 0 0 1 2 2v2a2 2 0 0 0 0 4v2a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2v-2a2 2 0 0 0 0-4Z" /><path d="M13 6v2M13 11v2M13 16v2" /></>,
  switch: <><path d="M4 8h12l-3-3" /><path d="M20 16H8l3 3" /></>,
  mail: <><rect x="3" y="5" width="18" height="14" rx="2" /><path d="m3 7 9 6 9-6" /></>,
  chevronLeft: <path d="m14 6-6 6 6 6" />,
  chevronRight: <path d="m10 6 6 6-6 6" />,
  image: <><rect x="3" y="5" width="18" height="14" rx="2" /><circle cx="8.5" cy="10" r="1.5" /><path d="m5 17 4.5-4.5 3 3L16 12l3 3" /></>,
  pencil: <><path d="M4 20h4L19 9a2.1 2.1 0 0 0-3-3L5 17Z" /><path d="m14.5 6.5 3 3" /></>,
  more: <><circle cx="5" cy="12" r="1.4" fill="currentColor" stroke="none" /><circle cx="12" cy="12" r="1.4" fill="currentColor" stroke="none" /><circle cx="19" cy="12" r="1.4" fill="currentColor" stroke="none" /></>,
  help: <><circle cx="12" cy="12" r="9" /><path d="M9.6 9.2a2.5 2.5 0 0 1 4.9.8c0 1.7-2.5 2-2.5 3.5" /><path d="M12 17v.5" /></>,
  book: <><path d="M5 4.5A1.5 1.5 0 0 1 6.5 3H19v15H6.5A1.5 1.5 0 0 0 5 19.5Z" /><path d="M5 19.5A1.5 1.5 0 0 1 6.5 18H19v3H6.5A1.5 1.5 0 0 1 5 19.5Z" /></>,
  palette: <><path d="M12 3a9 9 0 1 0 0 18c1.1 0 2-.9 2-2 0-.5-.2-1-.6-1.4-.3-.4-.4-.8-.4-1.1 0-.8.7-1.5 1.5-1.5H16a5 5 0 0 0 5-5c0-3.9-4-7-9-7Z" /><circle cx="7.5" cy="11.5" r="1.2" fill="currentColor" stroke="none" /><circle cx="11" cy="7.5" r="1.2" fill="currentColor" stroke="none" /><circle cx="15.5" cy="9" r="1.2" fill="currentColor" stroke="none" /></>,
  users: <><circle cx="9" cy="8" r="3.5" /><path d="M2.5 20a6.5 6.5 0 0 1 13 0" /><path d="M16 5.2a3.5 3.5 0 0 1 0 5.6" /><path d="M17.5 14.2a6.5 6.5 0 0 1 4 5.8" /></>,
};

export function Icon({
  name, size = 16, className, ...rest
}: { name: IconName; size?: number } & SVGProps<SVGSVGElement>) {
  return (
    <svg
      viewBox="0 0 24 24"
      width={size}
      height={size}
      fill="none"
      stroke="currentColor"
      strokeWidth="1.7"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
      className={className}
      {...rest}
    >
      {paths[name]}
    </svg>
  );
}
